import { MRT_ColumnFiltersState } from 'material-react-table';
import { FilterFieldConfig, TableFilterConfig } from './types';

/**
 * Creates an empty filter object with all fields set to null
 */
export const createEmptyFilters = <T extends Record<string, any>>(
  fields: FilterFieldConfig[]
): T => fields.reduce((acc, field) => {
  acc[field.id as keyof T] = null as any;
  return acc;
}, {} as T);

/**
 * Creates a filter object with default values
 */
export const createDefaultFilters = <T extends Record<string, any>>(
  fields: FilterFieldConfig[]
): T => fields.reduce((acc, field) => {
  acc[field.id as keyof T] = field.defaultValue ?? null;
  return acc;
}, {} as T);

/**
 * Retrieves and validates filter state from localStorage
 */
export const getStoredFilters = <T extends Record<string, any>>(
  localStorageKey: string,
  fields: FilterFieldConfig[]
): T => {
  try {
    const saved = localStorage.getItem(localStorageKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate that all required fields exist
      const fieldIds = fields.map(f => f.id);
      const hasAllFields = fieldIds.every(id => id in parsed);
      if (hasAllFields) {
        return parsed as T;
      }
    }
  } catch (e) {
    console.error(`[useTableFilters] Failed to parse localStorage for key "${localStorageKey}":`, e);
  }
  return createDefaultFilters<T>(fields);
};

/**
 * Saves filter state to localStorage
 */
export const saveFiltersToStorage = <T extends Record<string, any>>(
  localStorageKey: string,
  filters: T
): void => {
  try {
    localStorage.setItem(localStorageKey, JSON.stringify(filters));
  } catch (e) {
    console.error(`[useTableFilters] Failed to save to localStorage for key "${localStorageKey}":`, e);
  }
};

/**
 * Converts MRT column filters array to a filter object
 */
export const columnFiltersToObject = <T extends Record<string, any>>(
  columnFilters: MRT_ColumnFiltersState,
  fields: FilterFieldConfig[]
): Partial<T> => {
  const fieldIds = new Set(fields.map(f => f.id));
  return columnFilters
    .filter(cf => fieldIds.has(cf.id))
    .reduce<Partial<T>>((acc, { id, value }) => {
      (acc as Record<string, any>)[id] = value || null;
      return acc;
    }, {});
};

/**
 * Converts a filter object to MRT column filters array
 * Only includes non-null, non-empty values
 */
export const objectToColumnFilters = <T extends Record<string, any>>(
  filters: T,
  columnIdMap?: Record<string, string>
): MRT_ColumnFiltersState => Object.entries(filters)
  .filter(([, value]) => value !== null && value !== '')
  .map(([key, value]) => ({
    id: columnIdMap?.[key] || key,
    value: value as string
  }));

/**
 * Checks if there are any pending filter changes
 */
export const hasPendingFilterChanges = (
  columnFilters: MRT_ColumnFiltersState
): boolean => columnFilters.length > 0 &&
  columnFilters.some(filter => filter.value && filter.value !== '');

/**
 * Checks if any filters are currently active (non-null values)
 */
export const hasActiveFilterValues = <T extends Record<string, any>>(
  filters: T
): boolean => Object.values(filters).some(value => value !== null && value !== '');

/**
 * Builds the display name map from field configuration
 */
export const buildDisplayNameMap = <T extends Record<string, any>>(
  config: TableFilterConfig<T>
): Partial<Record<keyof T, string>> => {
  const mapFromFields = config.fields.reduce((acc, field) => {
    acc[field.id as keyof T] = field.label;
    return acc;
  }, {} as Partial<Record<keyof T, string>>);

  // Override with explicit displayNameMap if provided
  return {
    ...mapFromFields,
    ...config.displayNameMap
  };
};

/**
 * Builds query params from filters, applying API key mapping
 */
export const buildQueryParams = <T extends Record<string, any>>(
  filters: T,
  fields: FilterFieldConfig[]
): Record<string, any> => fields.reduce((acc, field) => {
  const value = filters[field.id as keyof T];
  if (value !== null && value !== undefined && value !== '') {
    const key = field.apiKey || field.id;
    return { ...acc, [key]: value };
  }
  return acc;
}, {} as Record<string, any>);

/**
 * Merges new filter values into existing filters
 */
export const mergeFilters = <T extends Record<string, any>>(
  existing: T,
  updates: Partial<T>,
  fields: FilterFieldConfig[]
): T => {
  const fieldIds = new Set(fields.map(f => f.id));
  return Object.entries(updates).reduce(
    (acc, [key, value]) => (fieldIds.has(key)
      ? { ...acc, [key]: value || null }
      : acc),
    { ...existing }
  );
};
